import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import { LocalGasStation } from "@mui/icons-material";
import { API_URL } from "../../utils/api-config";

const fuelTypes = ["Diesel", "Essence", "E85", "GPL", "Electrique"];

interface FuelCardAddTransactionProps {
  cardNumber: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function FuelCardAddTransaction({
  cardNumber,
  open,
  onClose,
  onSuccess,
}: FuelCardAddTransactionProps) {
  const [form, setForm] = useState({
    amount: "",
    liters: "",
    fuelType: "Diesel",
    location: "",
    mileage: "",
    driverId: "",
    vehiclePlate: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!form.amount || !form.liters || !form.location) {
      setError("Veuillez remplir tous les champs obligatoires");
      return;
    }

    const amount = parseFloat(form.amount);
    const liters = parseFloat(form.liters);
    const mileage = form.mileage ? parseInt(form.mileage) : 0;

    if (isNaN(amount) || amount <= 0) {
      setError("Le montant doit être un nombre positif");
      return;
    }

    if (isNaN(liters) || liters <= 0) {
      setError("La quantité de litres doit être un nombre positif");
      return;
    }

    setLoading(true);
    setSuccess(null);
    setError(null);

    // Préparer les données à envoyer
    const transactionData = {
      amount,
      liters,
      fuelType: form.fuelType,
      location: form.location,
      mileage: mileage || undefined,
      driverId: form.driverId || undefined,
      vehiclePlate: form.vehiclePlate || undefined,
    };

    console.log("🔹 Tentative d'ajout de transaction:", transactionData);
    console.log(
      "🔹 URL de l'API:",
      `${API_URL}/fuel-cards/${cardNumber}/transactions`
    );

    try {
      const res = await fetch(
        `${API_URL}/fuel-cards/${cardNumber}/transactions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(transactionData),
        }
      );

      const responseData = await res.json();
      console.log("🔹 Réponse de l'API:", responseData);

      if (!res.ok) {
        console.error("❌ Erreur API:", responseData);
        throw new Error(
          responseData.message || "Erreur lors de l'ajout de la transaction"
        );
      }

      console.log("✅ Transaction ajoutée avec succès:", responseData);
      setSuccess("Transaction ajoutée avec succès");

      // Réinitialiser le formulaire
      setForm({
        amount: "",
        liters: "",
        fuelType: "Diesel",
        location: "",
        mileage: "",
        driverId: "",
        vehiclePlate: "",
      });

      // Notifier le parent du succès
      if (onSuccess) {
        console.log("🔄 Notification du parent pour rafraîchir les données");
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("❌ Erreur lors de l'ajout de la transaction:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Erreur lors de l'ajout de la transaction"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        <LocalGasStation sx={{ mr: 1 }} />
        Ajouter une transaction
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="amount"
                label="Montant (€)"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="liters"
                label="Litres"
                name="liters"
                value={form.liters}
                onChange={handleChange}
                type="number"
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Grid>
            <Grid sx={{ gridColumn: "span 12" }}>
              <TextField
                select
                margin="normal"
                required
                fullWidth
                id="fuelType"
                label="Type de carburant"
                name="fuelType"
                value={form.fuelType}
                onChange={handleChange}
              >
                {fuelTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid sx={{ gridColumn: "span 12" }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="location"
                label="Station / Lieu"
                name="location"
                value={form.location}
                onChange={handleChange}
              />
            </Grid>
            <Grid sx={{ gridColumn: "span 12" }}>
              <TextField
                margin="normal"
                fullWidth
                id="mileage"
                label="Kilométrage (optionnel)"
                name="mileage"
                value={form.mileage}
                onChange={handleChange}
                type="number"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
              <TextField
                margin="normal"
                fullWidth
                id="driverId"
                label="ID Chauffeur (optionnel)"
                name="driverId"
                value={form.driverId}
                onChange={handleChange}
              />
            </Grid>
            <Grid sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
              <TextField
                margin="normal"
                fullWidth
                id="vehiclePlate"
                label="Plaque d'immatriculation (optionnel)"
                name="vehiclePlate"
                value={form.vehiclePlate}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Enregistrer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
